import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { PlatformSettingsResponse } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { PlatformTabs } from '../components/PlatformTabs';

export function PlatformSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'settings'],
    queryFn: () => api<PlatformSettingsResponse>('/platform/settings'),
  });

  const [maxAttempts, setMaxAttempts] = useState(10);
  const [windowMinutes, setWindowMinutes] = useState(15);
  useEffect(() => {
    if (data) {
      setMaxAttempts(data.loginRateLimit.maxAttempts);
      setWindowMinutes(data.loginRateLimit.windowMinutes);
    }
  }, [data]);

  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api<PlatformSettingsResponse>('/platform/settings', {
        method: 'PATCH',
        body: { loginRateLimit: { maxAttempts, windowMinutes } },
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      void qc.invalidateQueries({ queryKey: ['platform', 'settings'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  return (
    <div className="space-y-4">
      <PlatformTabs />
      <h1 className="hidden text-xl font-semibold md:block">Erweiterte Einstellungen</h1>

      <Card title="Login-Rate-Limit">
        <p className="mb-4 text-sm text-slate-500">
          Begrenzt, wie oft sich <em>jemand</em> innerhalb eines Zeitfensters mit{' '}
          <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">POST /auth/login</code> anmelden darf —
          gezählt getrennt pro IP-Adresse (schützt gegen automatisiertes Ausprobieren vieler E-Mail-Adressen)
          und pro versuchter E-Mail-Adresse (schützt gegen automatisiertes Erraten eines einzelnen Passworts).
          Wird eines der beiden Limits erreicht, blockiert der Server weitere Anmeldeversuche für den Rest des
          Zeitfensters — unabhängig davon, ob die Zugangsdaten richtig sind. Zu niedrig eingestellt, kann das
          normales Verhalten stören (z. B. ein Admin, der beim Testen mehrfach zwischen verschiedenen Konten
          von derselben Internetverbindung aus ein- und ausloggt); zu hoch eingestellt, schwächt es den Schutz
          gegen automatisiertes Ausprobieren von Zugangsdaten. Die Änderung gilt sofort für alle Benutzer,
          ohne Neustart.
        </p>

        {isLoading ? (
          <p className="text-sm text-slate-400">Lädt…</p>
        ) : (
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <Field label="Maximale Versuche">
              <Input
                type="number"
                min={3}
                max={1000}
                value={maxAttempts}
                onChange={(e) => setMaxAttempts(Number(e.target.value))}
                className="w-28"
                required
              />
            </Field>
            <Field label="Zeitfenster (Minuten)">
              <Input
                type="number"
                min={1}
                max={1440}
                value={windowMinutes}
                onChange={(e) => setWindowMinutes(Number(e.target.value))}
                className="w-28"
                required
              />
            </Field>
            <Button type="submit" disabled={save.isPending} className="self-end">
              {save.isPending ? 'Speichert…' : 'Speichern'}
            </Button>
            {saved && <span className="self-end text-sm text-green-600">Gespeichert ✓</span>}
          </form>
        )}
        {error && <Alert>{error}</Alert>}
      </Card>
    </div>
  );
}
